import { useQuery } from '@tanstack/react-query';
import { getSmartStartCards } from '../api';
import { useMemo } from 'react';
import type { UseSmartStartCardsOptions } from './useSmartStartCards.types';

/**
 * Hook for fetching smart start cards for a specific springboard tab.
 *
 * Focused responsibility: smart-start cards data fetching per tab.
 * Visibility filtering is INJECTED by the caller via `isAgentEnabled` so this
 * hook has zero cross-feature value imports and `features/springboard/**`
 * stays at zero `@/features/<other>` imports.
 */
export const useSmartStartCards = ({
  tabId,
  enabled = true,
  isAgentEnabled,
  canOpenAgent,
}: UseSmartStartCardsOptions) => {
  const query = useQuery({
    queryKey: ['smart-start-cards', tabId],
    queryFn: async () => {
      if (!tabId) {
        throw new Error('Tab ID is required');
      }
      return await getSmartStartCards(tabId);
    },
    enabled: enabled && !!tabId,
    retry: false,
    retryOnMount: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Filter cards to only show those with enabled agents the caller may open.
  // Both predicates are injected by the page so this hook stays pure
  // w.r.t. cross-feature/entity dependencies.
  const filteredCards = useMemo(() => {
    if (!query.data) return [];
    return query.data.filter(
      (card) => isAgentEnabled(card.input.agentId) && canOpenAgent(card.input.agentId),
    );
  }, [query.data, isAgentEnabled, canOpenAgent]);

  return {
    cards: filteredCards,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};
